import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useClerk, useUser } from "@clerk/react";
import { useStore } from "../store/globalState";
import { useConsumerIdentityStore } from "../store/useConsumerIdentityStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import {
  buildAuthPageUrl,
  serializeAuthResume,
  type AuthResume,
} from "../auth/resume";
import { clearConsumerIdentityBind } from "../auth/consumerIdentityCache";
import { usableGivenName } from "../utils/homeGreeting";
import { getEvents, resetAnalytics, safeCapture } from "../analytics";
import { TITLE_COLOR } from "../constants";

function initialsFromUser(
  fullName?: string | null,
  email?: string | null
): string {
  const name = fullName?.trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  const local = email?.split("@")[0]?.trim();
  if (local) return local.slice(0, 2).toUpperCase();
  return "?";
}

/**
 * Initials prefer the CIP / platform given name from bind, then Clerk profile.
 */
function initialsForAvatar(sources: {
  cipFirstName?: string | null;
  clerkFullName?: string | null;
  clerkFirstName?: string | null;
  clerkLastName?: string | null;
  email?: string | null;
}): string {
  const cip = usableGivenName(sources.cipFirstName);
  if (cip) return initialsFromUser(cip, null);

  const clerkName =
    [usableGivenName(sources.clerkFirstName), usableGivenName(sources.clerkLastName)]
      .filter(Boolean)
      .join(" ") || usableGivenName(sources.clerkFullName);
  return initialsFromUser(clerkName || null, sources.email);
}

/**
 * Signed-in session chrome for the class B home and claim entry. Sign-out is
 * in-widget auth — never partner callback, never Clerk UserButton / Account Portal.
 */
interface ConsumerSessionMenuProps {
  resume?: AuthResume;
  fromClaim?: boolean;
  analyticsScreen?: string;
}

export const ConsumerSessionMenu = memo(({
  resume = { kind: "transactions" },
  fromClaim = false,
  analyticsScreen = "home",
}: ConsumerSessionMenuProps) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { user } = useUser();
  const clerk = useClerk();
  const { linkId } = useRouteWithToken();
  const cipFirstName = useConsumerIdentityStore(
    (state) => state.bind?.payerFirstName,
  );
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, background, bodyCopy } = colorConfig;
  const fullName = user?.fullName ?? "";
  const email = user?.primaryEmailAddress?.emailAddress ?? "";
  // Clerk always sets imageUrl (default purple silhouette). Only hasImage
  // means the user uploaded a real photo — otherwise show themed initials.
  const hasCustomImage = Boolean(user?.hasImage && user.imageUrl);
  const imageUrl = user?.imageUrl;
  const initials = useMemo(
    () =>
      initialsForAvatar({
        cipFirstName,
        clerkFullName: fullName,
        clerkFirstName: user?.firstName,
        clerkLastName: user?.lastName,
        email,
      }),
    [cipFirstName, email, fullName, user?.firstName, user?.lastName],
  );
  const label =
    usableGivenName(cipFirstName) ||
    fullName ||
    email ||
    t("auth.accountMenu");

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const handleSignOut = useCallback(async () => {
    if (signingOut) return;
    setSigningOut(true);
    setOpen(false);
    safeCapture(getEvents().ACCOUNT_SIGN_OUT(analyticsScreen));
    clearConsumerIdentityBind();
    resetAnalytics();

    const redirectUrl = buildAuthPageUrl({
      origin: window.location.origin,
      linkId,
      resumeRaw: serializeAuthResume(resume),
      fromClaim,
    });

    try {
      await clerk.signOut({ redirectUrl });
    } catch (error) {
      console.error("[ConsumerSessionMenu] signOut failed:", error);
      window.location.assign(redirectUrl);
    }
  }, [analyticsScreen, clerk, fromClaim, linkId, resume.kind, signingOut]);

  return (
    <div ref={rootRef} className="consumer-session-menu">
      <button
        type="button"
        className="consumer-session-menu__trigger"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {hasCustomImage && imageUrl ? (
          <img
            className="consumer-session-menu__avatar"
            src={imageUrl}
            alt=""
          />
        ) : (
          <span
            className="consumer-session-menu__initials"
            style={{ background: backgroundBtn, color: textColorBtn }}
            aria-hidden
          >
            {initials}
          </span>
        )}
      </button>
      {open && (
        <div
          className="consumer-session-menu__dropdown"
          role="menu"
          style={{ background, color: TITLE_COLOR }}
        >
          {(fullName || email) && (
            <div className="consumer-session-menu__identity">
              {fullName ? (
                <p
                  className="consumer-session-menu__name"
                  style={{ color: TITLE_COLOR }}
                >
                  {fullName}
                </p>
              ) : null}
              {email ? (
                <p
                  className="consumer-session-menu__email"
                  style={{ color: bodyCopy }}
                >
                  {email}
                </p>
              ) : null}
            </div>
          )}
          <button
            type="button"
            role="menuitem"
            className="consumer-session-menu__sign-out"
            style={{ color: TITLE_COLOR }}
            disabled={signingOut}
            onClick={() => void handleSignOut()}
          >
            {t("auth.signOut")}
          </button>
        </div>
      )}
    </div>
  );
});
